import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Info, Clock, Calendar } from "lucide-react";

interface FormatInfoDialogProps {
  formatType: string;
  formatName: string;
  sport?: string;
  modality?: string;
  groupRules?: {
    setsPerMatch?: number;
    goldenPoint?: boolean;
    superTieBreak?: boolean;
    maxGamesPerSet?: number;
  };
  additionalRules?: {
    timing?: {
      warmupMinutes?: number;
      restMinutes?: number;
      sideChangeSeconds?: number;
    };
    conduct?: {
      toleranceMinutes?: number;
    };
    classification?: {
      firstPlaces?: number;
      rules?: string[];
    };
  };
  gameDays?: string[];
  startTime?: string;
  endTime?: string;
  isOpen: boolean;
  onClose: () => void;
}

const FormatInfoDialog = ({
  formatType,
  formatName,
  sport,
  modality,
  groupRules,
  additionalRules,
  gameDays,
  startTime,
  endTime,
  isOpen,
  onClose,
}: FormatInfoDialogProps) => {
  const normalizeFormat = (ft: string) => ft?.toLowerCase().replace('_', '-');
  const isEscaleraFormat = (ft: string) => {
    const n = normalizeFormat(ft);
    return n === 'escalera-piramidal' || n === 'escalera';
  };

  const getNormalizedModality = () => (modality?.toLowerCase() || '');

  // Helper to detect Pádel Singles Escalera
  const isPadelSinglesEscalera = () => {
    if (!isEscaleraFormat(formatType)) return false;
    const normalizedSport = sport?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
    const isSingles = getNormalizedModality() === 'singles' || getNormalizedModality() === 'individual';
    return normalizedSport === 'padel' && isSingles;
  };

  // Helper to detect Doubles Escalera (Rey de la Cancha)
  const isDoublesEscalera = () => {
    if (!isEscaleraFormat(formatType)) return false;
    const mod = getNormalizedModality();
    return mod === 'doubles' || mod === 'dobles';
  };

  const getFormatInfo = () => {
    const normalizedFormat = formatType?.toLowerCase().replace('_', '-');
    
    switch (normalizedFormat) {
      case 'round-robin':
        return {
          title: "Formato Round Robin (Todos contra Todos)",
          description: "En este formato, cada participante se enfrenta contra todos los demás participantes del grupo. El ganador se determina por el mayor número de victorias o puntos acumulados.",
          features: [
            "Todos los equipos juegan entre sí al menos una vez",
            "Garantiza un número mínimo de partidos para todos",
            "El ranking se determina por victorias totales",
            "Ideal para conocer el nivel real de cada participante"
          ],
          advantage: "Este formato garantiza que todos los participantes tengan múltiples oportunidades de jugar y competir."
        };
      
      case 'sumatoria-juegos':
      case 'sumatoria_juegos':
        return {
          title: "Formato Sumatoria de Juegos",
          description: "Similar al Round Robin, pero el ranking se determina por la suma total de juegos ganados en todos los partidos, no solo por victorias.",
          features: [
            "Todos juegan contra todos",
            "Se cuentan TODOS los juegos ganados",
            "Ejemplo: ganar 6-4, 6-3 suma 12 juegos",
            "Recompensa el rendimiento consistente"
          ],
          advantage: "Este sistema valora cada juego ganado, haciendo que cada punto cuente para el resultado final."
        };
      
      case 'escalera-piramidal':
      case 'escalera_piramidal':
      case 'escalera':
        // Check for Doubles Escalera (Rey de la Cancha)
        if (isDoublesEscalera()) {
          return {
            title: "Formato Escalera - Rey de la Cancha",
            description: "En este formato las parejas compiten en encuentros organizados por cancha. El número de cancha representa tu posición en el ranking. Las parejas de cada cancha se enfrentan y el ganador sube de cancha mientras el perdedor baja.",
            features: [
              "Las parejas se enfrentan por cancha en cada encuentro",
              "El ranking se refleja en el número de cancha asignada",
              "El ganador del encuentro sube a una cancha superior",
              "El perdedor baja a una cancha inferior",
              "Siempre se juegan encuentros, nunca hay semana sin competencia",
              "El objetivo es llegar y mantenerse en la Cancha 1"
            ],
            advantage: "Este formato garantiza competencia constante entre parejas, donde cada encuentro define tu posición en el ranking de canchas.",
            isDoublesEscalera: true
          };
        }
        // Check for special Pádel Singles case
        if (isPadelSinglesEscalera()) {
          return {
            title: "Formato Escalera de Pádel (Individual)",
            description: "En este formato dinámico, los jugadores individuales se enfrentan en canchas organizadas jerárquicamente. Cada encuentro te asigna una pareja diferente, fomentando la competencia individual mientras disfrutas del pádel en dobles.",
            features: [
              "En cada encuentro se te asigna una pareja aleatoria o rotativa",
              "Las canchas están organizadas de menor a mayor nivel",
              "El mejor jugador de cada cancha SUBE a la cancha superior",
              "El jugador con menor rendimiento BAJA a la cancha inferior",
              "El objetivo es llegar y mantenerse en la cancha principal",
              "Ideal para conocer diferentes compañeros mientras compites"
            ],
            advantage: "Este formato combina la competencia individual con la experiencia del pádel en parejas, permitiéndote conocer diferentes estilos de juego mientras compites por ascender en la clasificación.",
            isPadelSingles: true
          };
        }
        return {
          title: "Formato Escalera (Pirámide)",
          description: "Los jugadores se organizan en niveles jerárquicos. Puedes desafiar a jugadores del nivel superior. Si ganas, intercambias posiciones. El objetivo es llegar a la cima de la escalera.",
          features: [
            "Organización en niveles o posiciones",
            "Puedes desafiar jugadores superiores (normalmente hasta 2 posiciones arriba)",
            "Si ganas el desafío, intercambias posiciones",
            "Horarios flexibles - los jugadores coordinan sus partidos"
          ],
          advantage: "Formato ideal para ligas largas con flexibilidad de horarios, donde los jugadores pueden programar sus propios partidos."
        };
      
      case 'eliminacion-directa':
      case 'eliminacion_directa':
      case 'elimination':
        return {
          title: "Formato Eliminación Directa",
          description: "Formato de copa o knockout tradicional. Si pierdes un partido, quedas eliminado del torneo. El último jugador o equipo sin derrotas es el campeón.",
          features: [
            "Un solo partido elimina al perdedor",
            "Estructura de llaves eliminatorias",
            "Rondas: Inicial → Octavos → Cuartos → Semifinales → Final",
            "Alta emoción en cada partido"
          ],
          advantage: "Máxima tensión competitiva donde cada partido es decisivo para seguir en el torneo."
        };
      
      default:
        return {
          title: formatName,
          description: "Formato de competición personalizado.",
          features: [
            "Formato específico del evento",
            "Consulta con el organizador para más detalles"
          ],
          advantage: ""
        };
    }
  };

  const formatInfo = getFormatInfo();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <Info className="h-6 w-6 text-primary" />
            {formatInfo.title}
          </DialogTitle>
          <DialogDescription className="text-base pt-2">
            {formatInfo.description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 pt-4">
          {/* Features */}
          <div>
            <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              Características:
            </h3>
            <ul className="space-y-2">
              {formatInfo.features.map((feature, index) => (
                <li key={index} className="flex items-start gap-2 text-sm">
                  <span className="text-primary mt-1">•</span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Game Rules if available */}
          {groupRules && (
            <div className="border-t pt-4">
              <h3 className="font-semibold text-lg mb-3">⚙️ Configuración de Partidos</h3>
              <ul className="space-y-2 text-sm">
                {groupRules.setsPerMatch && (
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Sets por partido: <strong>{groupRules.setsPerMatch}</strong></span>
                  </li>
                )}
                {groupRules.maxGamesPerSet && (
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Juegos por set: <strong>{groupRules.maxGamesPerSet}</strong></span>
                  </li>
                )}
                {groupRules.goldenPoint !== undefined && (
                  <li className="flex items-start gap-2">
                    <span className={groupRules.goldenPoint ? "text-amber-600 mt-1" : "text-muted-foreground mt-1"}>•</span>
                    <span className={groupRules.goldenPoint ? "text-amber-600 font-medium" : ""}>
                      {groupRules.goldenPoint ? '⚡ Golden Point activado' : 'Golden Point: No'}
                    </span>
                  </li>
                )}
                {groupRules.superTieBreak !== undefined && (
                  <li className="flex items-start gap-2">
                    <span className={groupRules.superTieBreak ? "text-purple-600 mt-1" : "text-muted-foreground mt-1"}>•</span>
                    <span className={groupRules.superTieBreak ? "text-purple-600 font-medium" : ""}>
                      {groupRules.superTieBreak ? '🎯 Super Tiebreak activado' : 'Super Tiebreak: No'}
                    </span>
                  </li>
                )}
              </ul>
            </div>
          )}

          {/* Timing Rules */}
          {additionalRules?.timing && (
            <div className="border-t pt-4">
              <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" />
                Tiempos de Juego
              </h3>
              <ul className="space-y-2 text-sm">
                {additionalRules.timing.warmupMinutes && (
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Calentamiento: <strong>{additionalRules.timing.warmupMinutes} minutos</strong></span>
                  </li>
                )}
                {additionalRules.timing.restMinutes && (
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Descanso entre sets: <strong>{additionalRules.timing.restMinutes} minuto(s)</strong></span>
                  </li>
                )}
                {additionalRules.timing.sideChangeSeconds && (
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Cambio de lado: <strong>{additionalRules.timing.sideChangeSeconds} segundos</strong></span>
                  </li>
                )}
              </ul>
            </div>
          )}

          {/* Schedule and Days */}
          {(gameDays || startTime || endTime) && (
            <div className="border-t pt-4">
              <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-green-600" />
                Horarios y Días
              </h3>
              <ul className="space-y-2 text-sm">
                {gameDays && gameDays.length > 0 && (
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Días de juego: <strong>{gameDays.join(', ')}</strong></span>
                  </li>
                )}
                {(startTime || endTime) && (
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Horario: <strong>{startTime?.slice(0,5) || '--:--'} a {endTime?.slice(0,5) || '--:--'}</strong></span>
                  </li>
                )}
              </ul>
            </div>
          )}

          {/* Special section for Doubles Escalera (Rey de la Cancha) */}
          {isDoublesEscalera() && (
            <div className="border-t pt-4">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200 space-y-3">
                <h3 className="font-semibold text-lg text-blue-700 flex items-center gap-2">
                  👑 Sistema de Encuentros por Cancha
                </h3>
                <p className="text-sm text-blue-800">
                  En cada encuentro, las parejas asignadas a la misma cancha se enfrentan. El <strong>número de cancha define tu ranking</strong>: Cancha 1 es la posición más alta. Siempre hay encuentros programados.
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                    ⬆️ Ganador = Sube de cancha
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                    ⬇️ Perdedor = Baja de cancha
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Special section for Pádel Singles Escalera */}
          {isPadelSinglesEscalera() && (
            <div className="border-t pt-4">
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-4 rounded-lg border border-orange-200 space-y-3">
                <h3 className="font-semibold text-lg text-orange-700 flex items-center gap-2">
                  🔄 Sistema de Rotación de Parejas
                </h3>
                <p className="text-sm text-orange-800">
                  En cada encuentro jugarás con una <strong>pareja diferente</strong>. Esto te permite conocer diversos estilos de juego y adaptarte a distintos compañeros mientras compites de forma individual.
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                    ⬆️ Mejor = Sube de cancha
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                    ⬇️ Menor rendimiento = Baja
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Special section for Generic Escalera Piramidal (not Pádel Singles) */}
          {(formatType === 'escalera-piramidal' || formatType === 'escalera_piramidal' || formatType === 'escalera') && !isPadelSinglesEscalera() && (
            <div className="border-t pt-4">
              <div className="bg-purple-50 p-4 rounded-lg space-y-3">
                <h3 className="font-semibold text-lg text-purple-700 flex items-center gap-2">
                  🪜 Características Especiales de Escalera
                </h3>
                <ul className="space-y-2 text-sm">
                  {gameDays && gameDays.length > 0 ? (
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 mt-1">•</span>
                      <span>Partidos programados los: <strong>{gameDays.join(', ')}</strong></span>
                    </li>
                  ) : (
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 mt-1">✅</span>
                      <span><strong>Flexibilidad de fechas:</strong> Los jugadores pueden programar sus partidos en cualquier día acordado</span>
                    </li>
                  )}
                  {additionalRules?.conduct?.toleranceMinutes && (
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 mt-1">•</span>
                      <span>Tolerancia de llegada: <strong>{additionalRules.conduct.toleranceMinutes} minutos</strong></span>
                    </li>
                  )}
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 mt-1">📞</span>
                    <span><strong>Coordinación directa</strong> entre jugadores para programar partidos</span>
                  </li>
                </ul>
              </div>
            </div>
          )}

          {/* Conduct Rules (only if not Escalera, as it's already shown above) */}
          {additionalRules?.conduct?.toleranceMinutes && 
           formatType !== 'escalera-piramidal' && 
           formatType !== 'escalera_piramidal' && 
           formatType !== 'escalera' && (
            <div className="border-t pt-4">
              <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                ⚠️ Normas
              </h3>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>Tolerancia de llegada: <strong>{additionalRules.conduct.toleranceMinutes} minutos</strong></span>
                </li>
              </ul>
            </div>
          )}

          {/* Advantage */}
          {formatInfo.advantage && (
            <div className="border-t pt-4">
              <div className="bg-muted p-4 rounded-lg">
                <p className="text-sm font-medium text-muted-foreground">
                  ✨ {formatInfo.advantage}
                </p>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FormatInfoDialog;
